import {
  BOQ_ACCEPT,
  BOQ_MAX_BYTES,
  BOQ_OTHER,
  boqCategories,
  boqGroups,
  parseNum,
  specsText,
  t,
} from '@procumi/shared';
import { useRef, useState } from 'react';
import { useAsync } from '../hooks/useAsync';
import type { Toast } from '../hooks/useToast';
import { api, errMsg } from '../lib/api';
import { boqFromExtraction, boqState, setBoqState, type BoqRow, type BoqState } from '../lib/boq';
import type { Route } from '../routes';
import { rich } from '../lib/rich';

/**
 * Din antemăsurătoare la cereri de ofertă. Omul încarcă devizul, lista de
 * cantități sau o poză cu necesarul; citirea automată scoate pozițiile și le
 * împarte pe categoriile firmei. Aici le vede, le corectează și face câte o
 * cerere pe categorie. Nimic nu pleacă la furnizori de aici.
 */

/** O poziție de cofraj fără înălțime nu spune furnizorului ce să oferteze. */
const vagueCofraj = (r: BoqRow) => /cofra/i.test(r.desc) && !r.specs?.height;

/** Valoarea din lista de categorii care deschide caseta pentru o categorie nouă. */
const NEW_CATEGORY = '\u0000new';

export function BoqPage({ go, toast }: { go: (r: Route) => void; toast: Toast }) {
  const [st, setSt] = useState<BoqState | null>(() => boqState());
  const [reading, setReading] = useState<string | null>(null);
  const [pasting, setPasting] = useState(false);
  const [text, setText] = useState('');
  const [over, setOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const cats = useAsync(() => api.categories(), [], 'categories');
  // Folosite, cele noi scrise aici și cele propuse pentru meserie: orice poziție
  // din „Altele” se poate muta undeva și ajunge într-o cerere.
  const categories = [
    ...boqCategories(
      [...(cats.data?.used ?? []), ...(st?.rows.map((r) => r.category) ?? [])],
      cats.data?.suggested ?? [],
    ),
    BOQ_OTHER,
  ];
  // rândul la care se scrie o categorie nouă
  const [naming, setNaming] = useState<{ key: string; name: string } | null>(null);

  const save = (next: BoqState | null) => {
    setBoqState(next);
    setSt(next);
  };
  const setRow = (key: string, p: Partial<BoqRow>) =>
    st && save({ ...st, rows: st.rows.map((r) => (r.key === key ? { ...r, ...p } : r)) });

  const read = async (src: { file: File } | { text: string }, name: string) => {
    if ('file' in src && src.file.size > BOQ_MAX_BYTES) {
      toast(t('Fișierul e prea mare. Maximul e {0} MB.', Math.round(BOQ_MAX_BYTES / (1024 * 1024))));
      return;
    }
    setReading(name);
    try {
      const out = await api.extractBoq(src);
      if (!out.lines.length) {
        toast(t('N-am găsit poziții de cumpărat în document.'));
        return;
      }
      save(boqFromExtraction(out, name));
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setReading(null);
    }
  };

  const pick = (f: File | undefined) => {
    if (f) void read({ file: f }, f.name);
  };

  const makeRfq = (category: string, rows: BoqRow[]) => {
    const bad = rows.find((r) => r.on && !(parseNum(r.qtyText) ?? 0));
    if (bad) {
      toast(t('Scrie cantitatea pentru „{0}” sau scoate bifa.', bad.desc));
      return;
    }
    if (!rows.some((r) => r.on)) {
      toast(t('Bifează cel puțin o poziție.'));
      return;
    }
    // cantitățile corectate aici trec în cerere
    save({ ...st!, rows: st!.rows.map((r) => ({ ...r, qty: parseNum(r.qtyText) ?? r.qty })) });
    go({ view: 'new', fromBoq: category });
  };

  if (reading) {
    return (
      <>
        <Head />
        <section className="sheet boq-reading" aria-live="polite">
          <span className="boq-spin" aria-hidden="true" />
          <h2>{t('Citesc „{0}”', reading)}</h2>
          <p>
            {t(
              'Scot pozițiile, cantitățile și unitățile și le împart pe categorii. De obicei durează sub un minut; la o antemăsurătoare lungă, ceva mai mult.',
            )}
          </p>
        </section>
      </>
    );
  }

  if (!st) {
    return (
      <>
        <Head />
        <section className="sheet boq-start">
          {pasting ? (
            <div className="boq-paste">
              <label className="f" htmlFor="boq-text">
                {t('Lipește lista de cantități')}
                <textarea
                  id="boq-text"
                  className="inp"
                  rows={12}
                  autoFocus
                  value={text}
                  placeholder={t(
                    'ex.\nBeton C25/30 fundații	42 mc\nOțel BST500 Ø12	3,2 t\nCofraj pereți h 3 m	180 mp',
                  )}
                  onChange={(e) => setText(e.target.value)}
                />
              </label>
              <div className="boq-acts">
                <button
                  className="btn primary"
                  disabled={!text.trim()}
                  onClick={() => void read({ text }, t('textul lipit'))}
                >
                  {t('Citește lista')}
                </button>
                <button className="btn" onClick={() => setPasting(false)}>
                  {t('Încarc un fișier')}
                </button>
              </div>
            </div>
          ) : (
            <div
              className={`boq-drop${over ? ' over' : ''}`}
              onDragOver={(e) => {
                e.preventDefault();
                setOver(true);
              }}
              onDragLeave={() => setOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setOver(false);
                pick(e.dataTransfer.files[0]);
              }}
            >
              <span className="boq-ico" aria-hidden="true">
                <svg width="26" height="26" viewBox="0 0 24 24">
                  <path
                    d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8l-5-5ZM14 3v5h5M9 13h6M9 17h4"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              <h2>{t('Trage aici antemăsurătoarea')}</h2>
              <p>
                {t(
                  'Deviz, listă de cantități sau necesar: PDF, Excel (.xlsx, .xls), CSV sau o poză clară cu foaia. Până la {0} MB.',
                  Math.round(BOQ_MAX_BYTES / (1024 * 1024)),
                )}
              </p>
              <div className="boq-acts">
                <button className="btn primary" onClick={() => input.current?.click()}>
                  {t('Alege fișierul')}
                </button>
                <button className="btn" onClick={() => setPasting(true)}>
                  {t('Lipește textul')}
                </button>
              </div>
              <input
                ref={input}
                type="file"
                hidden
                accept={BOQ_ACCEPT}
                onChange={(e) => {
                  pick(e.target.files?.[0]);
                  e.target.value = '';
                }}
              />
            </div>
          )}
          <ul className="boq-how">
            <li>
              {rich('<b>1</b> Citesc pozițiile și le împart pe categoriile tale: Armătură, Beton, Cofraje…')}
            </li>
            <li>{rich('<b>2</b> Le verifici: corectezi cantitățile, scoți ce nu cumperi acum.')}</li>
            <li>
              {rich('<b>3</b> Faci câte o cerere pe categorie; furnizorii categoriei sunt deja bifați.')}
            </li>
          </ul>
        </section>
      </>
    );
  }

  const groups = boqGroups(st.rows);
  const total = st.rows.length;

  return (
    <>
      <Head>
        <button className="btn" onClick={() => save(null)}>
          {t('Alt document')}
        </button>
      </Head>

      <div className="boq-sum">
        <span>
          {rich(
            '<b>{0}</b> {1} în <b>{2}</b> {3}, din „{4}”{5}',
            total,
            total === 1 ? t('poziție') : t('poziții'),
            groups.length,
            groups.length === 1 ? t('categorie') : t('categorii'),
            st.fileName,
            st.title ? ` · ${st.title}` : '',
          )}
        </span>
      </div>

      {st.warnings.length ? (
        <div className="boq-warn" role="note">
          <b>{t('De verificat înainte să trimiți:')}</b>
          <ul>
            {st.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}

      <div className="boq-groups">
        {groups.map((g) => {
          const rows = g.lines as BoqRow[];
          const on = rows.filter((r) => r.on).length;
          const done = st.done[g.category];
          return (
            <section key={g.category} className={`sheet panel boq-group${done ? ' done' : ''}`}>
              <header className="panel-h">
                <h2>{g.category}</h2>
                <span className="panel-hint">
                  {on === rows.length ? rows.length : t('{0} din {1}', on, rows.length)}
                </span>
                <span className="panel-act">
                  {done ? (
                    <button className="btn small" onClick={() => go({ view: 'rfq', id: done })}>
                      <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true">
                        <path
                          d="m3.5 8.5 3 3 6-7"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="1.8"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                      {t('Cererea e făcută')}
                    </button>
                  ) : g.category === BOQ_OTHER ? (
                    <span className="boq-other">
                      {t('alege categoria pe fiecare rând sau „+ Categorie nouă”')}
                    </span>
                  ) : (
                    <button
                      className="btn small primary"
                      disabled={!on}
                      onClick={() => makeRfq(g.category, rows)}
                    >
                      {t('Fă cererea{0}', on ? ` (${on})` : '')}
                    </button>
                  )}
                </span>
              </header>
              <table className="boq-table">
                <thead>
                  <tr>
                    <th aria-label={t('Intră în cerere')} />
                    <th>{t('Poziție')}</th>
                    <th className="n">{t('Cantitate')}</th>
                    <th>{t('UM')}</th>
                    <th>{t('Categorie')}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const noQty = r.on && !(parseNum(r.qtyText) ?? 0);
                    const extra = [
                      r.code,
                      specsText(r.specs),
                      r.rentDays ? t('chirie {0} zile', r.rentDays) : '',
                      r.note,
                    ].filter(Boolean);
                    return (
                      <tr key={r.key} className={r.on ? undefined : 'off'}>
                        <td className="boq-chk">
                          <input
                            type="checkbox"
                            checked={r.on}
                            aria-label={t('Intră în cerere: {0}', r.desc)}
                            onChange={(e) => setRow(r.key, { on: e.target.checked })}
                          />
                        </td>
                        <td className="boq-desc">
                          <input
                            className="inp"
                            value={r.desc}
                            aria-label={t('Poziția')}
                            onChange={(e) => setRow(r.key, { desc: e.target.value })}
                          />
                          {extra.length ? <small>{extra.join(' · ')}</small> : null}
                          {r.on && vagueCofraj(r) ? (
                            <small className="boq-flag">
                              {t('fără înălțime: completeaz-o în cerere, la „Poziție de cofraj”')}
                            </small>
                          ) : null}
                        </td>
                        <td className="n">
                          <input
                            className={`inp${noQty ? ' boq-missing' : ''}`}
                            inputMode="decimal"
                            value={r.qtyText}
                            placeholder={t('lipsă')}
                            aria-label={t('Cantitatea pentru {0}', r.desc)}
                            onChange={(e) => setRow(r.key, { qtyText: e.target.value })}
                          />
                        </td>
                        <td className="boq-unit">
                          <input
                            className="inp"
                            value={r.unit}
                            aria-label={t('Unitatea de măsură')}
                            onChange={(e) => setRow(r.key, { unit: e.target.value })}
                          />
                        </td>
                        <td className="boq-cat">
                          {naming?.key === r.key ? (
                            <input
                              className="inp"
                              autoFocus
                              value={naming.name}
                              placeholder={t('Numele categoriei')}
                              aria-label={t('Categorie nouă pentru {0}', r.desc)}
                              maxLength={60}
                              onChange={(e) => setNaming({ key: r.key, name: e.target.value })}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') e.currentTarget.blur();
                                if (e.key === 'Escape') setNaming(null);
                              }}
                              onBlur={() => {
                                const name = naming.name.trim();
                                if (name && boqCategories([name], []).length) setRow(r.key, { category: name });
                                setNaming(null);
                              }}
                            />
                          ) : (
                            <select
                              className="inp"
                              value={r.category}
                              aria-label={t('Categoria pentru {0}', r.desc)}
                              onChange={(e) =>
                                e.target.value === NEW_CATEGORY
                                  ? setNaming({ key: r.key, name: '' })
                                  : setRow(r.key, { category: e.target.value })
                              }
                            >
                              {(categories.includes(r.category) ? categories : [...categories, r.category]).map(
                                (c) => (
                                  <option key={c} value={c}>
                                    {c}
                                  </option>
                                ),
                              )}
                              <option value={NEW_CATEGORY}>{t('+ Categorie nouă…')}</option>
                            </select>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </section>
          );
        })}
      </div>
    </>
  );
}

function Head({ children }: { children?: React.ReactNode }) {
  return (
    <div className="head">
      <div>
        <h1>{t('Din antemăsurătoare')}</h1>
        <p>
          {t(
            'Încarci devizul sau lista de cantități. Citesc pozițiile, le împart pe categorii și faci câte o cerere de ofertă pentru fiecare.',
          )}
        </p>
      </div>
      {children ? <div className="acts">{children}</div> : null}
    </div>
  );
}
